import { usernameClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { isNativePlatform } from '@/lib/native-features';

// Storage keys
export const BACKEND_URL_KEY = 'smas_backend_url';
export const TENANT_ID_KEY = 'smas_tenant_id';
const DEVICE_TOKEN_KEY = 'smas_device_token';

// Get base domain from environment or fallback
function getBaseDomain(): string {
  return import.meta.env.VITE_BASE_DOMAIN || 'opexmx.com';
}

// Build backend URL from tenant ID
export function buildBackendUrlFromTenant(tenantId: string): string {
  const baseDomain = getBaseDomain();
  // Remove protocol and trailing slashes from tenant ID if user included it
  const cleanTenantId = tenantId.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  // Build URL: https://tenant.opexmx.com
  return `https://${cleanTenantId}.${baseDomain}`;
}

// Get tenant ID from localStorage
export function getTenantId(): string | null {
  return localStorage.getItem(TENANT_ID_KEY);
}

// Set tenant ID and build the backend URL (with page reload)
export function setTenantId(tenantId: string): void {
  localStorage.setItem(TENANT_ID_KEY, tenantId);
  const backendUrl = buildBackendUrlFromTenant(tenantId);
  setBackendUrl(backendUrl);
}

// Set tenant ID and build the backend URL (without page reload)
// For use during login/register when saving tenant configuration
export function setTenantIdNoReload(tenantId: string): void {
  localStorage.setItem(TENANT_ID_KEY, tenantId);
  const backendUrl = buildBackendUrlFromTenant(tenantId);
  setBackendUrlNoReload(backendUrl);
}

// Clear tenant ID
export function clearTenantId() {
  localStorage.removeItem(TENANT_ID_KEY);
}

// Get backend URL from localStorage or use default
export function getBackendUrl(): string {
  const stored = localStorage.getItem(BACKEND_URL_KEY);
  if (stored?.trim()) {
    return `${stored.trim().replace(/\/$/, '')}/api`;
  }
  // Fallback to environment variable (works for both dev and production/mobile)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // In production web, use the current origin (frontend URL as base)
  return typeof window !== 'undefined' ? window.location.origin : '/api';
}

// Get the dynamic auth base URL
export function getAuthBaseUrl(): string {
  const apiBaseUrl = getBackendUrl();
  // better-auth auth endpoints are at /api/auth/*, so append /auth to the base URL
  return `${apiBaseUrl}/auth`;
}

// Create auth client factory that uses current backend URL
export function createDynamicAuthClient() {
  const authBaseUrl = getAuthBaseUrl();
  return createAuthClient({
    baseURL: authBaseUrl,
    plugins: [usernameClient()],
    fetchOptions: {
      credentials: 'include',
    },
    // On native (Capacitor), inject Bearer device token into every auth request
    // since cookies don't persist cross-origin on mobile
    ...(isNativePlatform()
      ? {
          auth: {
            type: 'Bearer' as const,
            token: () => localStorage.getItem(DEVICE_TOKEN_KEY) || '',
          },
        }
      : {}),
  });
}

// Re-export a convenience function that always returns a client with current backend URL
export const getAuthClient = createDynamicAuthClient;

// Legacy export for backward compatibility - always returns the dynamic client
export const authClient = createDynamicAuthClient();

// Set backend URL and trigger a page reload to apply changes
export function setBackendUrl(url: string): void {
  localStorage.setItem(BACKEND_URL_KEY, url);
  // Try to extract tenant ID from URL if it matches the base domain pattern
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const baseDomain = getBaseDomain();
    // Check if hostname matches pattern: tenant.baseDomain or tenant.baseDomain.com
    const tenantPattern = `.${baseDomain.replace(/\./g, '\\.')}$`;
    const match = hostname.match(new RegExp(`^(.+?)${tenantPattern}`));
    if (match?.[1]) {
      localStorage.setItem(TENANT_ID_KEY, match[1]);
    }
  } catch {
    // URL parsing failed, ignore
  }
  // Force navigation to login page with a full page load
  // This ensures all clients are re-initialized with the new URL
  // Using href instead of reload() to ensure we start from a clean state
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

// Set backend URL WITHOUT page reload - for use during login/register when
// saving tenant configuration. The auth client will be created with the new URL
// on the next authentication request.
export function setBackendUrlNoReload(url: string): void {
  localStorage.setItem(BACKEND_URL_KEY, url);
  // Try to extract tenant ID from URL if it matches the base domain pattern
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const baseDomain = getBaseDomain();
    // Check if hostname matches pattern: tenant.baseDomain or tenant.baseDomain.com
    const tenantPattern = `.${baseDomain.replace(/\./g, '\\.')}$`;
    const match = hostname.match(new RegExp(`^(.+?)${tenantPattern}`));
    if (match?.[1]) {
      localStorage.setItem(TENANT_ID_KEY, match[1]);
    }
  } catch {
    // URL parsing failed, ignore
  }
}

// Clear backend URL setting
export function clearBackendUrl() {
  localStorage.removeItem(BACKEND_URL_KEY);
  clearTenantId();
  clearDeviceToken();
}

// Get current backend URL setting
export function getBackendUrlSetting(): string {
  return localStorage.getItem(BACKEND_URL_KEY) || '';
}

// Get R2 base URL for serving files
export function getR2BaseUrl(): string {
  const tenantId = getTenantId();
  if (!tenantId) {
    // Fallback for development or when tenant is not set
    return `https://r2-checklist.${getBaseDomain()}/`;
  }
  return `https://r2-${tenantId}.${getBaseDomain()}/`;
}

// Module-level session token store (set by AuthProvider from useSession)
let _cachedSessionToken: string | null = null;

export function setCachedSessionToken(token: string | null): void {
  _cachedSessionToken = token;
}

// Device token (API key) for native mobile — stored in localStorage
export function getDeviceToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(DEVICE_TOKEN_KEY);
}

export function setDeviceToken(token: string): void {
  localStorage.setItem(DEVICE_TOKEN_KEY, token);
}

export function clearDeviceToken(): void {
  localStorage.removeItem(DEVICE_TOKEN_KEY);
}

export function getSessionToken(): string | null {
  if (_cachedSessionToken) return _cachedSessionToken;
  // On native, fall back to device token (API key) when session cookie isn't available
  if (isNativePlatform()) {
    const deviceToken = getDeviceToken();
    if (deviceToken) return deviceToken;
  }
  if (typeof window === 'undefined') return null;
  return null;
}
